import { useEffect, useMemo, useState, type ReactNode, type RefObject } from 'react';
import {
  Box,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import {
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  Tune as PropertyIcon,
  Visibility as HighlightIcon,
} from '@mui/icons-material';
import type { ValidationIssueFamily } from '../lib/validationIssueFamilies';
import {
  resolveValidationPriorityMeta,
  resolveVisibleValidationNumber,
} from '../validationConstants';
import {
  buildPreviewList,
  formatCategory,
  pluralize,
  resolveFamilyCountLabel,
  resolvePrimaryValidationId,
  resolvePrimaryValidationTitle,
} from '../lib/validationPresentation';
import { getReconnectAllValidationId } from '../lib/validationReconnectActionRules';
import { scopePipingSpecQuickFixToFamily } from '../lib/validationQuickFixScope';
import type {
  ValidationDecisionAction,
  ValidationSwitchPathGuidance,
} from '../lib/validationNextStepRouting';
import type { ValidationRepairPanelTab } from '../../../shared/stores/usePcfValidationStore';
import { resolveValidationIssueDecision } from '../lib/validationNextStepRouting';
import { buildValidationRepairDetailItems } from '../lib/validationRepairDetailModel';
import {
  isDirectValidationRepairOption,
} from '../lib/validationRepairOptions';
import SeverityBadge from './SeverityBadge';
import ValidationPriorityBadge from './ValidationPriorityBadge';
import ValidationRawIssueList from './ValidationRawIssueList';
import { QuickFixButton } from './QuickFixButton';
import { useValidationIssueRepairActions } from '../hooks/useValidationIssueRepairActions';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import {
  OperationProgressWindowRequest,
} from '../../../shared/ui/progress';
import {
  WorkspaceActionBar,
  WorkspaceMetricCard,
  WorkspaceMetricGrid,
  WorkspacePane,
  WorkspaceSection,
  WorkspaceStatusPill,
  getWorkflowWorkspacePalette,
} from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import WorkspacePanel from '../../../shared/ui/workflow/WorkspacePanelPrimitives';
import WorkspaceEmptyState from '../../../shared/ui/workflow/WorkspaceEmptyState';
import { AccordionSection, ScrollablePanel } from '../../../shared/ui/layout';
import { SectionHeader, StatusChip } from '../../../shared/ui/common';
import { buildEndTypeSlotSwapQuickFillFamily } from '../lib/validationEndTypeSlotSwapQuickFillHandoff';
import { BodyText, CaptionText, CardTitle, OverlineText, SubsectionTitle } from '../../../shared/ui/typography';

interface ValidationIssueDetailPanelProps {
  family: ValidationIssueFamily | null;
  displayNumber?: number | null;
  onExecutePrimaryAction: (family: ValidationIssueFamily) => void;
  onOpenRepairDetail?: (
    family: ValidationIssueFamily,
    options?: {
      activeTab?: ValidationRepairPanelTab;
      surgeryMode?: boolean;
    }
  ) => void;
  onOpenConditionStudio?: (family: ValidationIssueFamily) => void;
  onHighlightElements: (elementIds: string[]) => void;
  onReconnectPair?: (elementId1: string, elementId2: string) => Promise<void> | void;
  onReconnectAll?: (validationId: number) => Promise<void> | void;
  onRunValidation: () => void;
  onRunValidationScoped?: (validationIds: number[]) => Promise<void> | void;
  onRunFocusedValidation?: (family: ValidationIssueFamily) => Promise<void> | void;
  focusedValidationRunning?: boolean;
  onCopyIssueList: (family: ValidationIssueFamily) => void;
  canHighlightElements?: boolean;
  canQuickFix?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateSteps?: string[];
  emptyStateActionLabel?: string;
  onEmptyStateAction?: () => void;
  tourRefs?: {
    recommendedRef?: RefObject<HTMLDivElement | null>;
    quickFixRef?: RefObject<HTMLDivElement | null>;
    classificationRef?: RefObject<HTMLDivElement | null>;
    fallbackGuidanceRef?: RefObject<HTMLDivElement | null>;
    alternatePathsRef?: RefObject<HTMLDivElement | null>;
    impactSummaryRef?: RefObject<HTMLDivElement | null>;
    moreDetailsRef?: RefObject<HTMLDivElement | null>;
    affectedItemsRef?: RefObject<HTMLDivElement | null>;
    topAffectedTypesRef?: RefObject<HTMLDivElement | null>;
    sampleInstancesRef?: RefObject<HTMLDivElement | null>;
    rawIssueDetailsRef?: RefObject<HTMLDivElement | null>;
    copyActionRef?: RefObject<HTMLDivElement | null>;
  };
}

function SectionFrame({
  title,
  children,
  tone = 'default',
}: {
  title: string;
  children: ReactNode;
  tone?: 'default' | 'accent' | 'quiet';
}) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);

  return (
    <WorkspaceSection
      title={title}
      tone={tone === 'accent' ? 'accent' : 'neutral'}
      sx={tone === 'quiet' ? { backgroundColor: workspacePalette.paneBackground } : undefined}
    >
      {children}
    </WorkspaceSection>
  );
}
function SignalList({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box>
      <CaptionText sx={{
        display: 'block',
        color: workspacePalette.bodyText
      }}>
        {title}
      </CaptionText>
      <Stack spacing={layoutGap.cozy} sx={{ mt: 0.55 }}>
        {items.map((item) => (
          <BodyText key={`signal-${item}`} sx={{ color: workspacePalette.bodyText }}>
            • {item}
          </BodyText>
        ))}
      </Stack>
    </Box>
  );
}
function AlternateActionRow({
  action,
  onExecute,
  disabled,
  compact = false,
}: {
  action: ValidationDecisionAction;
  onExecute: () => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);

  return (
    <Box
      sx={{
        px: compact ? 0 : 0.95,
        py: compact ? 0 : 0.8,
        borderRadius: `${borderRadius.sm}px`,
        border: compact ? 'none' : `1px solid ${workspacePalette.sectionBorder}`,
        backgroundColor: compact ? 'transparent' : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.58 : 0.16),
      }}
    >
      <Stack direction="row" spacing={layoutGap.slant} justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <BodyText sx={{ display: 'block' }}>
            {action.label}
          </BodyText>
          <BodyText secondary sx={{
            display: 'block',
            mt: 0.3,
            color: workspacePalette.bodyText,
          }}>
            {action.reason}
          </BodyText>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={onExecute}
          disabled={disabled}
          startIcon={action.kind === 'rerun' ? <RunIcon /> : action.kind === 'highlight' ? <HighlightIcon /> : undefined}
          sx={{
            minWidth: 0,
            px: 0,
            color: workspacePalette.mutedText,
            justifyContent: 'flex-end',
          }}
        >
          Open
        </Button>
      </Stack>
    </Box>
  );
}
function SwitchGuidanceRow({
  guidance,
}: {
  guidance: ValidationSwitchPathGuidance;
}) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);

  return (
    <Box
      sx={{
        px: 0.95,
        py: 0.85,
        borderRadius: `${borderRadius.sm}px`,
        border: `1px solid ${workspacePalette.sectionBorder}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.58 : 0.16),
      }}
    >
      <BodyText sx={{ display: 'block' }}>
        {guidance.label}
      </BodyText>
      <Stack spacing={layoutGap.snug} sx={{ mt: 0.5 }}>
        {guidance.signals.map((signal) => (
          <BodyText key={`${guidance.label}-${signal}`} secondary sx={{ color: workspacePalette.bodyText }}>
            • {signal}
          </BodyText>
        ))}
      </Stack>
    </Box>
  );
}

export default function ValidationIssueDetailPanel({
  family,
  displayNumber = null,
  onExecutePrimaryAction,
  onOpenRepairDetail,
  onOpenConditionStudio,
  onHighlightElements,
  onReconnectPair,
  onReconnectAll,
  onRunValidation,
  onRunValidationScoped,
  onRunFocusedValidation,
  focusedValidationRunning = false,
  onCopyIssueList,
  canHighlightElements = true,
  canQuickFix = false,
  emptyStateTitle = 'Select an issue family',
  emptyStateDescription = 'Select an issue family to inspect what is affected and where to fix it.',
  emptyStateSteps,
  emptyStateActionLabel,
  onEmptyStateAction,
  tourRefs,
}: ValidationIssueDetailPanelProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const [showAffectedItems, setShowAffectedItems] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [showTypes, setShowTypes] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [showInstances, setShowInstances] = useState(false);
  const [showAllInstances, setShowAllInstances] = useState(false);
  const [showSwitchGuidance, setShowSwitchGuidance] = useState(false);
  const [showRawIssues, setShowRawIssues] = useState(false);

  useEffect(() => {
    setShowAffectedItems(false);
    setShowAllItems(false);
    setShowTypes(false);
    setShowAllTypes(false);
    setShowInstances(false);
    setShowAllInstances(false);
    setShowRawIssues(false);
  }, [family?.id]);

  const visibleAffectedItems = useMemo(() => {
    if (!family) {
      return [];
    }

    return showAllItems ? family.affectedItems : family.affectedItems.slice(0, 8);
  }, [family, showAllItems]);

  const visibleFailingInstances = useMemo(() => {
    if (!family) {
      return [];
    }

    return showAllInstances ? family.failingInstances : family.failingInstances.slice(0, 3);
  }, [family, showAllInstances]);

  const visibleExamples = useMemo(() => {
    if (!family) {
      return [];
    }

    return family.examples.slice(0, 3);
  }, [family]);

  const visibleFailingTypes = useMemo(() => {
    if (!family) {
      return [];
    }

    return showAllTypes ? family.failingTypes : family.failingTypes.slice(0, 4);
  }, [family, showAllTypes]);

  const decision = useMemo(() => (family ? resolveValidationIssueDecision(family) : null), [family]);
  const endTypeSlotSwapQuickFillFamily = useMemo(
    () => (family ? buildEndTypeSlotSwapQuickFillFamily(family) : null),
    [family]
  );
  const priorityMeta = useMemo(
    () => (family ? resolveValidationPriorityMeta(family.validationIds) : null),
    [family]
  );
  const resolvedDisplayNumber =
    displayNumber ?? (family ? resolveVisibleValidationNumber(family.validationIds) : null);
  const primaryValidationTitle = useMemo(
    () => (family ? resolvePrimaryValidationTitle(family) : ''),
    [family]
  );
  const primaryValidationId = useMemo(
    () => (family ? resolvePrimaryValidationId(family) : null),
    [family]
  );
  const canRunFocusedValidation =
    Boolean(onRunFocusedValidation) && primaryValidationId != null;
  const repairableItems = useMemo(
    () =>
      family
        ? buildValidationRepairDetailItems(family).filter(
            (item) => item.repairOptions.length > 0
          )
        : [],
    [family]
  );
  const primaryRepairableItem = repairableItems[0] ?? null;
  const primaryDirectRepairItem = useMemo(
    () =>
      repairableItems.find(
        (item) =>
          item.rawIssue.ElementIds.length > 0 &&
          item.repairOptions.some(isDirectValidationRepairOption)
      ) ?? null,
    [repairableItems]
  );
  const familyReconnectAllAction = useMemo(() => {
    if (!family) {
      return null;
    }

    const reconnectValidationId = getReconnectAllValidationId(family.rawIssues);

    if (reconnectValidationId) {
      return {
        label: 'Reconnect all',
        validationId: reconnectValidationId,
        description:
          'Attempt to reconnect every eligible connector pair in this issue family, then run validation again.',
      };
    }

    return null;
  }, [family]);

  const primaryActionOpensRepairWorkspace = decision?.primaryAction.kind === 'detail';
  const {
    repairWorkspaceActions,
    runAffectedValidations,
    undoingQuickFix,
    undoProgress,
  } = useValidationIssueRepairActions({
    family,
    familyReconnectAllAction,
    primaryActionOpensRepairWorkspace,
    onOpenRepairDetail,
    onReconnectAll,
    onRunValidationScoped,
  });
  const inlineQuickFixItem = primaryDirectRepairItem && canQuickFix ? primaryDirectRepairItem : null;
  const hasInlineQuickFix = Boolean(inlineQuickFixItem);
  const shouldShowQuickFixSection = hasInlineQuickFix || repairWorkspaceActions.length > 0;

  useEffect(() => {
    setShowSwitchGuidance(Boolean(decision?.switchGuidanceDefaultOpen));
  }, [decision?.switchGuidanceDefaultOpen, family?.id]);

  if (!family) {
    return (
      <WorkspacePane
        dataTestId="validation-issue-detail-panel-root"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.75,
          py: 2,
          color: theme.palette.text.secondary,
        }}
      >
        <WorkspaceEmptyState
          dataTestId="validation-issue-empty-state"
          title={emptyStateTitle}
          description={emptyStateDescription}
          steps={emptyStateSteps}
          actionLabel={emptyStateActionLabel}
          onAction={onEmptyStateAction}
          tone="neutral"
        />
      </WorkspacePane>
    );
  }

  const executeAction = (
    action: NonNullable<typeof decision>['primaryAction'],
    options?: {
      surgeryMode?: boolean;
      activeTab?: ValidationRepairPanelTab;
    }
  ) => {
    if (!decision) {
      return;
    }

    switch (action.kind) {
      case 'detail':
        onOpenRepairDetail?.(family, {
          activeTab: options?.activeTab ?? (options?.surgeryMode ? 'surgery' : 'recommended'),
          surgeryMode: options?.surgeryMode ?? false,
        });
        return;
      case 'condition':
        onOpenConditionStudio?.(family);
        return;
      case 'highlight':
        onHighlightElements(family.impactedElementIds);
        return;
      case 'rerun':
        onRunValidation();
        return;
      default:
        onExecutePrimaryAction(family);
    }
  };

  const alternateActions = decision?.alternateActions ?? [];
  const hasMoreDetails =
    family.affectedItems.length > 0 ||
    family.failingTypes.length > 0 ||
    family.failingInstances.length > 0 ||
    visibleExamples.length > 0 ||
    family.rawIssues.length > 0;

  return (
    <>
      <WorkspacePane
      dataTestId="validation-issue-detail-panel-root"
      sx={{
        flexShrink: 0,
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: workspacePalette.paneBackground,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 1.25,
          borderBottom: `1px solid ${workspacePalette.paneDivider}`,
          backgroundColor: workspacePalette.sectionRaisedBackground,
        }}
      >
        <Stack spacing={layoutGap.plate}>
          {/* Title and severity badges */}
          <Stack direction="row" spacing={layoutGap.row} alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <SubsectionTitle sx={{ color: workspacePalette.headingText }}>
                {resolvedDisplayNumber ? `Validation #${resolvedDisplayNumber} · ${primaryValidationTitle}` : primaryValidationTitle}
              </SubsectionTitle>
            </Box>
            <Stack direction="row" spacing={layoutGap.dense} flexShrink={0} alignItems="center">
              <SeverityBadge severity={family.severity} validationIds={family.validationIds} compact />
              {family.isBlocking && (
                <StatusChip tone="error" label="BLOCKER" />
              )}
            </Stack>
          </Stack>
          
          {/* Summary */}
          <BodyText sx={{
            color: workspacePalette.bodyText,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {family.summary}
          </BodyText>
          
          {/* Category and routing info */}
          <Stack direction="row" spacing={layoutGap.dense} flexWrap="wrap" useFlexGap alignItems="center">
            <WorkspaceStatusPill
              label={formatCategory(family.category)}
              tone="accent"
            />
            <WorkspaceStatusPill
              label={decision?.pathLabel ?? 'Review'}
              tone="neutral"
            />
            <ValidationPriorityBadge validationIds={family.validationIds} compact showRank={false} />
          </Stack>
          
          {/* Confidence indicator */}
          <CaptionText sx={{ display: 'block', color: workspacePalette.tertiaryText }}>
            {decision?.confidenceLabel} confidence · {decision?.pathReason}
          </CaptionText>
        </Stack>
      </Box>

      <ScrollablePanel sx={{ px: 1.5, py: 1.15 }}>
        <Stack spacing={layoutGap.span}>
          {/* Primary Action - Most prominent section */}
          <Box ref={tourRefs?.recommendedRef} data-testid="validation-tour-target-detail-recommended">
            <WorkspacePanel
              dataTestId="validation-detail-recommended-action"
              tone="primary"
              sx={{ p: 1.25 }}
            >
              <Stack spacing={layoutGap.slant}>
                <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
                  <OverlineText sx={{ color: theme.palette.primary.main }}>
                    Recommended action
                  </OverlineText>
                </Stack>
                
                <CardTitle sx={{ color: workspacePalette.headingText }}>
                  {decision?.primaryAction.label ?? 'Review issue details'}
                </CardTitle>
                
                {decision?.primaryActionSummary && (
                  <BodyText sx={{ color: workspacePalette.bodyText }}>
                    {decision.primaryActionSummary}
                  </BodyText>
                )}
                
                <WorkspaceActionBar
                  actions={[
                    {
                      label: decision?.primaryAction.label ?? 'Open',
                      tone: 'primary',
                      onClick: () =>
                        decision
                          ? executeAction(decision.primaryAction, {
                              surgeryMode: decision.prefersSurgery,
                            })
                          : undefined,
                    },
                    ...(onRunFocusedValidation
                      ? [
                          {
                            label: 'Run focused validation',
                            tone: 'secondary' as const,
                            startIcon: <RunIcon />,
                            help: validationActionTooltips.runFocusedValidation,
                            disabled: focusedValidationRunning || !canRunFocusedValidation,
                            onClick: () => {
                              if (!canRunFocusedValidation) {
                                return;
                              }

                              void onRunFocusedValidation(family);
                            },
                          },
                        ]
                      : []),
                    ...(endTypeSlotSwapQuickFillFamily
                      ? [
                          {
                            label: 'Open Quick Fill for safe slot swaps',
                            tone: 'secondary' as const,
                            startIcon: <PropertyIcon />,
                            help: validationActionTooltips.openSafeSlotSwapQuickFill,
                            onClick: () => onExecutePrimaryAction(endTypeSlotSwapQuickFillFamily),
                          },
                        ]
                      : []),
                  ]}
                  minActionWidth={200}
                />
                
                {decision?.compactSwitchGuidance && (
                  <CaptionText sx={{ color: workspacePalette.tertiaryText }}>
                    {decision.compactSwitchGuidance}
                  </CaptionText>
                )}
              </Stack>
            </WorkspacePanel>
          </Box>

          {shouldShowQuickFixSection ? (
            <Box ref={tourRefs?.quickFixRef} data-testid="validation-tour-target-detail-quick-fix">
              <SectionFrame title="Quick fix options" tone="accent">
                <Stack spacing={layoutGap.plate}>
                  {inlineQuickFixItem ? (
                    <WorkspacePanel
                      dataTestId="validation-detail-inline-quick-fix"
                      tone="success"
                      sx={{ p: 1 }}
                    >
                      <OverlineText sx={{
                        display: 'block',
                        color: theme.palette.success.main,
                        mb: 0.5
                      }}>
                        One-Click Fix Available
                      </OverlineText>
                      <QuickFixButton
                        issue={scopePipingSpecQuickFixToFamily(inlineQuickFixItem.rawIssue, family.impactedElementIds)}
                        onFixApplied={runAffectedValidations}
                        enabled={canQuickFix}
                        familyId={family.id}
                        snapshotLabel={`${family.title} quick fix`}
                      />
                    </WorkspacePanel>
                  ) : (
                    <BodyText sx={{ color: workspacePalette.bodyText }}>
                      {canQuickFix 
                        ? 'Open the repair workspace for guided fixes on this issue.'
                        : 'Use the repair workspace to review and fix this issue.'}
                    </BodyText>
                  )}

                  {repairWorkspaceActions.length > 0 && (
                    <WorkspaceActionBar
                      dataTestId="validation-detail-repair-actions"
                      actions={repairWorkspaceActions}
                      minActionWidth={160}
                    />
                  )}
                </Stack>
              </SectionFrame>
            </Box>
          ) : null}

          {/* Issue Details - Compact summary */}
          <Box ref={tourRefs?.classificationRef} data-testid="validation-tour-target-detail-classification">
            <SectionFrame title="Issue details">
              <Stack spacing={layoutGap.comfort}>
                <BodyText sx={{ color: workspacePalette.bodyText }}>
                  {decision?.unresolvedReason ?? family.summary}
                </BodyText>
                <WorkspaceMetricGrid minWidth={140}>
                  <WorkspaceMetricCard label="Type" value={formatCategory(family.category)} />
                  <WorkspaceMetricCard
                    label="Priority"
                    value={priorityMeta?.urgencyLabel ?? '-'}
                  />
                  <WorkspaceMetricCard label="Impact" value={resolveFamilyCountLabel(family)} />
                  <WorkspaceMetricCard label="Issues" value={`${family.issueCount} raw`} />
                </WorkspaceMetricGrid>
              </Stack>
            </SectionFrame>
          </Box>

          {/* Why This Path - Collapsed by default */}
          {decision && decision.routingDensity !== 'high' && decision.whyThisPath.length > 0 ? (
            <SectionFrame title="Why this path" tone="quiet">
              <SignalList 
                title={decision.pathType === 'scope-review' ? 'Scope review recommended' : 'Direct repair recommended'} 
                items={decision.whyThisPath} 
              />
            </SectionFrame>
          ) : null}

          {decision?.switchGuidance.length ? (
            <Box ref={tourRefs?.fallbackGuidanceRef} data-testid="validation-tour-target-detail-fallback-guidance">
              <SectionFrame
                title={decision.switchGuidanceDefaultOpen ? 'When to switch paths' : 'Fallback guidance'}
                tone="quiet"
              >
                <Stack spacing={layoutGap.loft}>
                  <Button
                    size="small"
                    onClick={() => setShowSwitchGuidance((current) => !current)}
                    sx={{ minWidth: 0, px: 0, alignSelf: 'flex-start' }}
                  >
                    {showSwitchGuidance ? 'Hide switch guidance' : 'Show switch guidance'}
                  </Button>
                  {showSwitchGuidance ? (
                    <Stack spacing={layoutGap.dense}>
                      {decision.switchGuidance.map((guidance) => (
                        <SwitchGuidanceRow key={`${guidance.pathType}-${guidance.label}`} guidance={guidance} />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </SectionFrame>
            </Box>
          ) : null}

          {/* Alternate Paths - Only show if there are valid alternatives */}
          {alternateActions.length > 0 && (
            <Box ref={tourRefs?.alternatePathsRef} data-testid="validation-tour-target-detail-alternate-paths">
              <SectionFrame title="Other options" tone="quiet">
                <Stack spacing={layoutGap.flush}>
                  {alternateActions
                    .filter((action) => {
                      const isConditionUnavailable = action.kind === 'condition' && !decision?.conditionStudioAllowed;
                      const isHighlightAction = action.kind === 'highlight' && family.impactedElementIds.length === 0;
                      return !isConditionUnavailable && !isHighlightAction;
                    })
                    .map((action) => (
                      <AlternateActionRow
                        key={`${action.kind}-${action.label}`}
                        action={action}
                        compact
                        onExecute={() => executeAction(action)}
                        disabled={action.kind === 'highlight' && !canHighlightElements}
                      />
                    ))}
                </Stack>
              </SectionFrame>
            </Box>
          )}

          {/* Impact Summary */}
          <Box ref={tourRefs?.impactSummaryRef} data-testid="validation-tour-target-detail-impact-summary">
            <SectionFrame title="Impact summary">
              <WorkspaceMetricGrid minWidth={160}>
                <WorkspaceMetricCard 
                          label="Affected items"
                  value={buildPreviewList(family.affectedItems)} 
                />
                {family.failingTypes.length > 0 && (
                  <WorkspaceMetricCard
                    label="Types"
                    value={buildPreviewList(family.failingTypes.map((item) => item.componentType))}
                  />
                )}
                {family.failingInstances.length > 0 && (
                  <WorkspaceMetricCard
                    label="Instances"
                    value={buildPreviewList(family.failingInstances.map((item) => item.label), 2)}
                  />
                )}
                {family.impactedElementIds.length > 0 && (
                  <WorkspaceMetricCard
                    label="Element IDs"
                    value={buildPreviewList(family.impactedElementIds, 2)}
                  />
                )}
              </WorkspaceMetricGrid>
            </SectionFrame>
          </Box>

          {hasMoreDetails && (
            <Stack
              ref={tourRefs?.moreDetailsRef}
              data-testid="validation-tour-target-detail-more-details"
              spacing={layoutGap.rise}
            >
              <SectionHeader label="More details" />

              {family.affectedItems.length > 0 && (
                <Box ref={tourRefs?.affectedItemsRef} data-testid="validation-tour-target-detail-affected-items">
                  <AccordionSection
                    title={family.category === 'connectors' ? 'Impacted items' : 'Affected fields and items'}
                    expanded={showAffectedItems}
                    onChange={setShowAffectedItems}
                    summary={buildPreviewList(family.affectedItems)}
                  >
                    <Stack spacing={layoutGap.dense}>
                      {visibleAffectedItems.map((item) => (
                        <BodyText key={item} sx={{ color: workspacePalette.bodyText }}>
                          {item}
                        </BodyText>
                      ))}
                    </Stack>
                    {family.affectedItems.length > 8 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllItems((current) => !current)}
                        sx={{ mt: 0.45, px: 0 }}
                      >
                        {showAllItems ? 'Show fewer items' : `View all ${pluralize(family.affectedItems.length, 'affected item')}`}
                      </Button>
                    )}
                  </AccordionSection>
                </Box>
              )}

              {family.failingTypes.length > 0 && (
                <Box ref={tourRefs?.topAffectedTypesRef} data-testid="validation-tour-target-detail-top-affected-types">
                  <AccordionSection
                    title="Top affected types"
                    expanded={showTypes}
                    onChange={setShowTypes}
                    summary={`${pluralize(family.failingTypes.length, 'type')} in this issue family`}
                  >
                    <Stack spacing={layoutGap.dense}>
                      {visibleFailingTypes.map((item) => (
                        <BodyText key={item.componentType} sx={{ color: workspacePalette.bodyText }}>
                          {item.componentType} · {pluralize(item.instanceCount, 'instance')} · {pluralize(item.issueCount, 'issue')}
                        </BodyText>
                      ))}
                    </Stack>
                    {family.failingTypes.length > 4 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllTypes((current) => !current)}
                        sx={{ mt: 0.45, px: 0 }}
                      >
                        {showAllTypes ? 'Show fewer types' : `View all ${pluralize(family.failingTypes.length, 'type')}`}
                      </Button>
                    )}
                  </AccordionSection>
                </Box>
              )}

              {(family.failingInstances.length > 0 || visibleExamples.length > 0) && (
                <Box ref={tourRefs?.sampleInstancesRef} data-testid="validation-tour-target-detail-sample-instances">
                  <AccordionSection
                    title={family.failingInstances.length > 0 ? 'Sample impacted instances' : 'Example failures'}
                    expanded={showInstances}
                    onChange={setShowInstances}
                    summary={
                      family.failingInstances.length > 0
                        ? pluralize(family.failingInstances.length, 'sampled instance')
                        : pluralize(visibleExamples.length, 'example')
                    }
                  >
                    <Stack spacing={layoutGap.dense}>
                      {family.failingInstances.length > 0
                        ? visibleFailingInstances.map((instance) => (
                            <BodyText key={`${instance.label}-${instance.reason}`} sx={{ color: workspacePalette.bodyText }}>
                              {instance.label} · {instance.reason}
                              {instance.elementIds.length > 0 ? ` · IDs: ${instance.elementIds.join(', ')}` : ''}
                            </BodyText>
                          ))
                        : visibleExamples.map((example) => (
                            <BodyText key={`${example.label}-${example.detail}`} sx={{ color: workspacePalette.bodyText }}>
                              {example.label} · {example.detail}
                              {example.elementIds.length > 0 ? ` · IDs: ${example.elementIds.join(', ')}` : ''}
                            </BodyText>
                          ))}
                    </Stack>
                    {family.failingInstances.length > 3 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllInstances((current) => !current)}
                        sx={{ mt: 0.45, px: 0 }}
                      >
                        {showAllInstances
                          ? 'Show fewer instances'
                          : `View all ${pluralize(family.failingInstances.length, 'failing instance')}`}
                      </Button>
                    )}
                  </AccordionSection>
                </Box>
              )}

              <Box ref={tourRefs?.rawIssueDetailsRef} data-testid="validation-tour-target-detail-raw-issue-details">
                <SectionHeader label="Raw issue details" />
                <Box sx={{ mt: 0.45 }}>
                  <AccordionSection
                    title="Raw issue details"
                    expanded={showRawIssues}
                    onChange={setShowRawIssues}
                    summary={`${pluralize(family.rawIssues.length, 'raw issue')} available`}
                  >
                    <ValidationRawIssueList key={family.id} issues={family.rawIssues} />
                  </AccordionSection>
                </Box>
              </Box>
            </Stack>
          )}

          <Box ref={tourRefs?.copyActionRef} data-testid="validation-tour-target-detail-copy-action">
            <ActionHelpTooltip help={validationActionTooltips.copyIssueList}>
              <Button
                variant="text"
                startIcon={<CopyIcon />}
                onClick={() => onCopyIssueList(family)}
                sx={{
                  minWidth: 0,
                  px: 0,
                  color: workspacePalette.mutedText,
                  alignSelf: 'flex-start',
                }}
              >
                Copy issue list
              </Button>
            </ActionHelpTooltip>
          </Box>
        </Stack>
      </ScrollablePanel>
      </WorkspacePane>
      <OperationProgressWindowRequest
        id="validation-issue-undo-quick-fix"
        open={undoingQuickFix}
        priority={80}
        model={
          undoProgress ?? {
            title: 'Undo quick fix',
            message: 'Quick fix undo queued for Revit.',
            status: 'running',
            percent: null,
            locked: true,
          }
        }
      />
    </>
  );
}
